import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { Box, IconButton, Skeleton, Tooltip, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useNotification } from "app/NotificationProvider";
import { BORDER_RADIUS } from "app/theme";
import { useTranslation } from "common/locales";
import { queryKeys } from "common/queryKeys";
import { getAllFileEntriesForUser } from "services";
import { downloadFileHelper } from "../../fileManagement/downloadFileHelper";

function useFarmerFileEntries(farmerGuid: string) {
  return useQuery({
    queryKey: queryKeys.fileEntries.byUser(farmerGuid),
    queryFn: async () => {
      const entries = await getAllFileEntriesForUser({
        associatedUserId: farmerGuid,
      });
      return entries.sort(
        (a, b) =>
          Date.parse(b.creationDate ?? "") - Date.parse(a.creationDate ?? "")
      );
    },
    enabled: !!farmerGuid,
  });
}

export default function FarmerFilesTable({
  farmerGuid,
}: {
  farmerGuid: string;
}) {
  const { data: files, isLoading } = useFarmerFileEntries(farmerGuid);
  const { notify } = useNotification();
  const { t } = useTranslation();

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: BORDER_RADIUS.small + "px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
        mb: 3,
      }}
    >
      <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #f0f0f0" }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "#18181b" }}
        >
          Files
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 2fr 1fr auto",
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: "1px solid #f0f0f0",
          backgroundColor: "#fafafa",
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "#71717a" }}
        >
          Type
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "#71717a" }}
        >
          File name
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "#71717a" }}
        >
          Uploaded
        </Typography>
        <Typography variant="caption" />
      </Box>

      {isLoading ? (
        [1, 2, 3].map((i) => (
          <Box
            key={i}
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr 1fr auto",
              gap: 1,
              px: 2,
              py: 1.5,
              borderBottom: "1px solid #f4f4f5",
            }}
          >
            <Skeleton variant="text" width={120} height={18} />
            <Skeleton variant="text" width={160} height={18} />
            <Skeleton variant="text" width={80} height={18} />
            <Skeleton variant="circular" width={28} height={28} />
          </Box>
        ))
      ) : !files || files.length === 0 ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No files uploaded yet
          </Typography>
        </Box>
      ) : (
        files.map((file) => (
          <Box
            key={file.guid}
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr 1fr auto",
              gap: 1,
              px: 2,
              py: 1.5,
              borderBottom: "1px solid #f4f4f5",
              alignItems: "center",
              "&:last-child": { borderBottom: "none" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <InsertDriveFileIcon
                sx={{ color: "#71717a", fontSize: 16, flexShrink: 0 }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: "#71717a",
                  fontSize: "0.8rem",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {t(`fileEntries.fileUploadType.label.${file.fileUploadType}`)}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "#18181b",
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {file.fileName || file.fileDescription || "-"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#71717a", fontSize: "0.8rem" }}
            >
              {formatDate(file.creationDate)}
            </Typography>
            <Tooltip title="Download">
              <IconButton
                size="small"
                onClick={() => downloadFileHelper(file.guid, notify)}
                sx={{
                  padding: "6px",
                  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ))
      )}
    </Box>
  );
}
